import { useNuxtApp, useRuntimeConfig } from '#imports'
import { type MaybeRefOrGetter, toValue } from 'vue'

import type {
  BasePageContent,
  MappedApiPageContent,
} from '@backmarket/http-api/src/api-specs-content/models/base-page-content'
import {
  useAddCacheTagHeader,
  useAddRawCacheTagHeader,
  useAddServiceCacheTagHeader,
} from '@backmarket/nuxt-module-http-cache/composables/useAddCacheTagHeader'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { toRfc4646Locale } from '@backmarket/utils/string/toRfc4646Locale'

export function useSetCMSPageCacheTagHeader() {
  // eslint-disable-next-line prefer-destructuring
  const environment = useRuntimeConfig().public?.httpcache?.environment
  const locale = useI18nLocale()
  const logger = useLogger()
  const app = useNuxtApp()

  function setCMSCacheTagHeader(
    data?: Pick<
      MappedApiPageContent<BasePageContent>,
      'pageName' | 'pageType'
    > | null,
  ) {
    if (!data) return

    // toRfc4646Locale transforms "fr-fr" into "fr-FR"
    // This formatting is required to comply with the PubSub event format, which expects locales in RFC 4646-compliant casing.
    try {
      const tag = `${environment}-cms-${toRfc4646Locale(locale)}-${data.pageType}-${data.pageName}`

      app.runWithContext(() => {
        useAddCacheTagHeader(tag)
        useAddServiceCacheTagHeader()
      })
    } catch (error) {
      logger.error('[Cache-Tag] Error setting Cache-Tag header in CMS page', {
        owners: ['bot-squad-content-front'],
        error: error as Error,
      })
    }
  }

  return { setCMSCacheTagHeader }
}

export function useAddSectionsCacheTagHeader(
  sectionId?: MaybeRefOrGetter<string | undefined>,
) {
  const logger = useLogger()
  const locale = useI18nLocale()

  try {
    const { environment } = useRuntimeConfig().public.httpcache
    const rfcLocale = toRfc4646Locale(locale)

    // Pass a getter (not the resolved value): on the Mod PDP the source is an
    // un-awaited `useAsyncData` and is still empty at call time. `useAddRawCacheTagHeader`
    // watches it and appends the tag once the section id resolves, before the SSR
    // response is sent. Empty/undefined ids are a no-op. No-op on the client.
    useAddRawCacheTagHeader(() => {
      const id = toValue(sectionId)

      return id
        ? `${environment}-cms-page-sections-${rfcLocale}-${id}`
        : undefined
    })
  } catch (error) {
    logger.error('[Cache-Tag] Error setting Cache-Tag header', {
      owners: ['bot-squad-content-front'],
      error: error as Error,
    })
  }
}
